import { Tooltip } from 'antd';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../App.css';
import TopNav from '../Components/TopNav';
import { useAuth } from '../contexts/AuthContext';
import { PageFooter } from './HomePage';
import MetadataManager from '../Components/MetadataManager';

import '../styles/dashboard.css';
const DashboardPage = () => {
  const { user, logs } = useAuth();
  // const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    percentage: 0
  });

  useEffect(() => {
    if (user && user.quotaResetDate) {
      const calculateTimeRemaining = () => {
        const now = new Date();
        const resetDate = new Date(user.quotaResetDate);
        const timeDiff = resetDate - now;
        
        // If reset date is in the past, show 0 time remaining
        if (timeDiff < 0) {
          setTimeRemaining({ days: 0, hours: 0, minutes: 0, percentage: 100 });
          return;
        }
        
        // Calculate remaining time
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        
        // Assuming a typical monthly cycle of 30 days
        const totalCycleDuration = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
        const elapsedTime = totalCycleDuration - timeDiff;
        const percentage = Math.min(100, Math.max(0, (elapsedTime / totalCycleDuration) * 100));
        
        setTimeRemaining({ days, hours, minutes, percentage });
      };
      
      calculateTimeRemaining();
      
      // Update the countdown every minute
      const intervalId = setInterval(calculateTimeRemaining, 60000);
      
      return () => clearInterval(intervalId);
    }
  }, [user]);

  if (!user) {
    navigate('/login');
    return null;
  }
  console.log(user);
  return (
    <>
      <MetadataManager 
        title="Dashboard | MenuPortal"
        description="Manage your MenuPortal API integration from your developer dashboard. View usage statistics and API key information."
        keywords="API dashboard, developer console, API usage, analytics, API key management"
      />
      <div className="dashboard-container main">
        <TopNav dark={true} transparent={false} addClass="dashboard-navbar"/>
        <div className="container main-content mt-5">
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>API Dashboard</h1>
                <Link to="/account" className="btn btn-outline-primary">
                  Account Settings
                </Link>
              </div>
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="card-title">Your API Keys</h5>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Key Name</th>
                          <th>API Key</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><div className='text-capitalize m-2'>API Key</div></td>
                          <td className='d-flex align-items-center justify-content-flex-start'>
                            <pre className='p-2 m-0'><code className='dark pl-5 pr-5 d-inline-block'>{user.apiKey}</code></pre>
                            <Tooltip trigger={['click']} title="Copied!">
                              <button onClick={() =>{navigator.clipboard.writeText(user.apiKey); }} className='btn btn-sm btn-outline-secondary'>Copy</button>
                            </Tooltip>
                          </td>
                          <td>
                            <span className="badge m-2 bg-success p-2 font-size-1em" >Active</span>
                          </td>
                        </tr>
                        {/* <tr>
                          <td>Test Key</td>
                          <td>
                            <code>mp_test_xxxxx</code>
                          </td>
                          <td>
                            <span className="badge bg-secondary">Test</span>
                          </td>
                        </tr> */}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="card" key={user.quotaUsage}>
                <div className="card-body">
                  <h5 className="card-title">API Usage</h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div className="border rounded p-3 text-center">
                        <h6>Total Requests</h6>
                        <h3>{user.quotaUsage}</h3>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="border rounded p-3 text-center">
                        <h6>Quota Usage</h6>
                        <h3>{user.quotaUsage}/{user.quotaLimit}</h3>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="border rounded p-3 text-center">
                        <h6>Plan</h6>
                        <h3 className='text-capitalize'>{user.plan}</h3>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <h6>Quota Reset in: {user.quotaResetDate ? 
                      `${timeRemaining.days} days, ${timeRemaining.hours} hours, ${timeRemaining.minutes} minutes` : 
                      'Loading...'}
                    </h6>
                    <div className="progress" style={{ height: '20px' }}>
                      <div 
                        className="progress-bar" 
                        role="progressbar" 
                        style={{ width: `${timeRemaining.percentage}%` }} 
                        aria-valuenow={timeRemaining.percentage} 
                        aria-valuemin="0" 
                        aria-valuemax="100"
                      >
                        {Math.round(timeRemaining.percentage)}% of cycle completed
                      </div>
                    </div>
                    {user.quotaResetDate && (
                      <div className="text-muted mt-1 small">
                        Next reset: {new Date(user.quotaResetDate).toLocaleDateString()} at {new Date(user.quotaResetDate).toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <h5 className="card-title">Access Logs</h5>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Endpoint</th>
                          {/* <th>Status</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {logs && logs.map((log) => (
                          <tr key={log.id}>
                            <td>{new Date(log.timestamp).toDateString()}</td>
                            <td>{new Date(log.timestamp).toLocaleTimeString()}</td>
                            <td><code className='dark'>{log.endpoint}</code></td>
                            {/* <td>
                              <span className={`badge ${log.status === 'success' ? 'bg-success' : 'bg-danger'}`}>{log.status}</span>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PageFooter />
    </>
  );
};

export default DashboardPage;